import React, { useEffect, useState } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Box,
    Select,
    MenuItem,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Tooltip,
    TextField,
} from "@mui/material";
import { SaveAlt as SaveAltIcon } from "@mui/icons-material";
import axios from "axios";
import { saveAs } from "file-saver";
import qr from "qrcode";


const EventParticipants = ({ shareIdentifier }) => {
    const [participants, setParticipants] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        round: "",
        status: "",
        participation: "",
        paymentStatus: "",
    });

    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

    const [selected, setSelected] = useState([]);
    const [smsText, setSmsText] = useState("");

    const [openExportDialog, setOpenExportDialog] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([
        "first_name",
        "last_name",
        "email",
        "phone",
        "round_name",
        "ticketType",
        "ticketStatus",
        "ticketParticipantStatus",
        "reservedPrice",
        "payment_amount",
        "payment_status",
        "transaction_id",
        "qrCodeImage",
        "acceptRejectDate",
        "isCodeused"
    ]);

    const columnLabels = {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone: "Phone",
        round_name: "Round",
        ticketType: "Ticket Type",
        ticketStatus: "Status",
        ticketParticipantStatus: "Participation",
        reservedPrice: "Price",
        payment_amount: "Payment",
        payment_status: "Payment Status",
        transaction_id: "Transaction ID",
        qrCodeImage: "Ticket Link",
        acceptRejectDate: "Accepted At",
        isCodeused: "Scanned",
    };
    const [resending, setResending] = useState(false);


    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/events/${shareIdentifier}/participants`
                );
                setParticipants(response.data);
            } catch (error) {
                console.error("❌ Failed to fetch participants:", error);
            } finally {
                setLoading(false);
            }
        };

        if (shareIdentifier) {
            fetchParticipants();
        }
    }, [shareIdentifier]);

    useEffect(() => {
        let data = [...participants];

        if (filters.round) {
            data = data.filter((p) => p.round_name === filters.round);
        }
        if (filters.status) {
            data = data.filter((p) => p.ticketStatus === filters.status);
        }
        if (filters.participation) {
            data = data.filter((p) => p.ticketParticipantStatus === filters.participation);
        }
        if (filters.paymentStatus) {
            data = data.filter((p) => p.payment_status === filters.paymentStatus);
        }

        if (sortConfig.key) {
            data.sort((a, b) => {
                const aVal = a[sortConfig.key] || "";
                const bVal = b[sortConfig.key] || "";

                if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
        }

        setFiltered(data);
        setSelected([]); // 🔁 Reset selection on filter
    }, [participants, filters, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const uniqueValues = (field) =>
        [...new Set(participants.map((p) => p[field]).filter(Boolean))];

    const toggleColumn = (key) => {
        setSelectedColumns((prev) =>
            prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
        );
    };

    const toggleSelect = (ticketId) => {
        setSelected((prev) =>
            prev.includes(ticketId)
                ? prev.filter((id) => id !== ticketId)
                : [...prev, ticketId]
        );
    };

    const handleSelectAll = () => {
        if (selected.length === filtered.length) {
            setSelected([]);
        } else {
            setSelected(filtered.map((p) => p.ticket_id));
        }
    };

    const handleExport = () => {
        const headers = selectedColumns.map((key) => columnLabels[key]);
        const rows = filtered.map((row) =>
            selectedColumns.map((key) => {
                const value = row[key];
                if (value instanceof Date) return value.toLocaleString();
                if (value === null || value === undefined) return "-";
                return String(value);
            })
        );

        const csvContent = [headers, ...rows]
            .map((row) => row.map((v) => `"${v}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        saveAs(blob, `participants-${shareIdentifier}.csv`);

        setOpenExportDialog(false);
    };

    const handleSendSMS = async () => {
        const selectedParticipants = filtered.filter((p) =>
          selected.includes(p.ticket_id)
        );
      
        if (!smsText.trim() || selectedParticipants.length === 0) {
          alert("Please select participants and enter a message.");
          return;
        }
      
        const report = [
          ["First Name", "Last Name", "Phone", "Message"],
          ...selectedParticipants.map((p) => [
            p.first_name,
            p.last_name,
            p.phone,
            smsText,
          ]),
        ];
      
        const csv = report.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        saveAs(blob, `sms-report-${shareIdentifier}.csv`);
      
        try {
          const phones = selectedParticipants.map((p) => p.phone).filter(Boolean);
      
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/activeTrail/send-sms`, {
            recipients: phones,
            message: smsText.trim(),
          });
      
          if (response.status === 200) {
            alert(`📨 SMS sent to ${selectedParticipants.length} participants`);
            setSmsText("");
            setSelected([]); // Reset selection
          } else {
            alert("❌ SMS sending failed");
          }
        } catch (error) {
          console.error("❌ Error sending SMS:", error);
          alert("❌ Failed to send SMS. Check server logs for details.");
        }
    };

    const handleResendTicket = async () => {
        const selectedParticipants = filtered.filter((p) =>
            selected.includes(p.ticket_id)
        );

        if (selectedParticipants.length === 0) {
            alert("Please select at least one participant.");
            return;
        }

        try {
            setResending(true);

            const report = [["First Name", "Last Name", "Phone", "Message"]];

            for (const p of selectedParticipants) {
                if (!p.phone || !p.qrCodeImage) continue;
        
                const smsMessage = `You can access your ticket using this link\n${p.qrCodeImage}`;

                // Add to report before sending
                report.push([p.first_name, p.last_name, p.phone, smsMessage]);

                // Send individual SMS
                try {
                    console.log("📱 smsMessage:", JSON.stringify(smsMessage, null, 2));

                    // Fixed URL - removed the extra character
                    const res = await axios.post(`${process.env.REACT_APP_API_URL}/activeTrail/send-sms`, {
                        recipients: [p.phone],
                        message: smsMessage.trim(),
                    });

                    if (res.status !== 200) {
                        console.warn(`⚠️ SMS failed for ${p.phone}`);
                    }
                } catch (err) {
                    console.error(`❌ Failed to send to ${p.phone}`, err);
                }
        }

            // Save CSV report
            const csv = report.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            saveAs(blob, `ticket-resend-report-${shareIdentifier}.csv`);

            alert(`📨 Resent ticket links to ${report.length - 1} participants`);
            setSelected([]);
        } catch (err) {
            console.error("❌ Resend Ticket Error:", err);
            alert("❌ Failed to resend tickets.");
        } finally {
            setResending(false);
        }
    };

    return (
        <Box p={2} sx={{ backgroundColor: "background.default", color: "text.primary" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Event Participants</Typography>
                <Tooltip title="Export">
                    <IconButton onClick={() => setOpenExportDialog(true)}>
                        <SaveAltIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <Select
                    value={filters.round}
                    onChange={(e) => setFilters({ ...filters, round: e.target.value })}
                    displayEmpty
                    size="small"
                >
                    <MenuItem value="">All Rounds</MenuItem>
                    {uniqueValues("round_name").map((val) => (
                        <MenuItem key={val} value={val}>
                            {val}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    displayEmpty
                    size="small"
                >
                    <MenuItem value="">All Statuses</MenuItem>
                    {uniqueValues("ticketStatus").map((val) => (
                        <MenuItem key={val} value={val}>
                            {val}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    value={filters.participation}
                    onChange={(e) =>
                        setFilters({ ...filters, participation: e.target.value })
                    }
                    displayEmpty
                    size="small"
                >
                    <MenuItem value="">All Participation</MenuItem>
                    {uniqueValues("ticketParticipantStatus").map((val) => (
                        <MenuItem key={val} value={val}>
                            {val}
                        </MenuItem>
                    ))}
                </Select>

                <Select
                    value={filters.paymentStatus}
                    onChange={(e) =>
                        setFilters({ ...filters, paymentStatus: e.target.value })
                    }
                    displayEmpty
                    size="small"
                >
                    <MenuItem value="">All Payment Statuses</MenuItem>
                    {uniqueValues("payment_status").map((val) => (
                        <MenuItem key={val} value={val}>
                            {val}
                        </MenuItem>
                    ))}
                </Select>
            </Box>


            {loading ? (
                <Box display="flex" justifyContent="center" mt={4} >
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: "70vh",
                        backgroundColor: "background.default", // 👈 force dark bg
                        color: "text.primary", // 👈 force dark text
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.length === filtered.length && filtered.length > 0}
                                        indeterminate={
                                            selected.length > 0 && selected.length < filtered.length
                                        }
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                                {selectedColumns.map((key) => (
                                    <TableCell
                                        key={key}
                                        onClick={() =>
                                            ["round_name", "ticketStatus", "ticketParticipantStatus", "payment_status"].includes(key) &&
                                            handleSort(key)
                                        }
                                        sx={{ cursor: "pointer" }}
                                    >
                                        {columnLabels[key]}
                                        {sortConfig.key === key
                                            ? sortConfig.direction === "asc"
                                                ? " 🔼"
                                                : " 🔽"
                                            : ""}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((p) => (
                                <TableRow key={p.ticket_id}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selected.includes(p.ticket_id)}
                                            onChange={() => toggleSelect(p.ticket_id)}
                                        />
                                    </TableCell>
                                    {selectedColumns.map((key) => (
                                        <TableCell key={key}>
                                            {["reservationAt", "acceptRejectDate"].includes(key)
                                                ? p[key]
                                                    ? new Date(p[key]).toLocaleString()
                                                    : "-"
                                                : key === "isCodeused"
                                                    ? p[key] === true ? "yes" : "no"
                                                : p[key] ?? "-"}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={selectedColumns.length + 1} align="center">
                                        No participants found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}



            {/* 📨 SMS Section (below table) */}
            <Box display="flex" alignItems="flex-start" flexDirection="column" gap={2} my={3} sx={{ backgroundColor: "background.default", color: "text.primary" }}>
                <Typography variant="subtitle1">Send SMS to Selected Participants</Typography>
                <TextField
                    label="SMS Text"
                    multiline
                    rows={10}
                    value={smsText}
                    onChange={(e) => setSmsText(e.target.value)}
                    sx={{ width: "15%" }} // 👈 30% width here
                />
                <Box display="flex" gap={2}>
                <Button
                    variant="contained"
                    disabled={selected.length === 0 || !smsText.trim()}
                    onClick={handleSendSMS}
                >
                    Send SMS
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={selected.length === 0 || resending}
                        onClick={handleResendTicket}
                    >
                        {resending ? "Sending..." : "Resend Ticket"}
                </Button>
                </Box>

            </Box>


            {/* 📦 Export Dialog */}
            <Dialog
                open={openExportDialog}
                onClose={() => setOpenExportDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select Columns to Export</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        {Object.keys(columnLabels).map((key) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Checkbox
                                        checked={selectedColumns.includes(key)}
                                        onChange={() => toggleColumn(key)}
                                    />
                                }
                                label={columnLabels[key]}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenExportDialog(false)}>Cancel</Button>
                    <Button onClick={handleExport} variant="contained">
                        Export CSV
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openExportDialog}
                onClose={() => setOpenExportDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Select Columns to Export</DialogTitle>
                <DialogContent>
                    <FormGroup>
                        {Object.keys(columnLabels).map((key) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Checkbox
                                        checked={selectedColumns.includes(key)}
                                        onChange={() => toggleColumn(key)}
                                    />
                                }
                                label={columnLabels[key]}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenExportDialog(false)}>Cancel</Button>
                    <Button onClick={handleExport} variant="contained">
                        Export CSV
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EventParticipants;
